import { IconNames } from '@blueprintjs/icons';
import { DialogWithForm } from '@strata/react-dialog';
import { InlineMessage } from '@strata/react-inline-message';
import { TextAreaFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

const ExceptionTypes = {
    DIAGNOSIS: 'DIAGNOSIS',
    POLICY: 'POLICY',
    PLACE_OF_SERVICE: 'PLACE_OF_SERVICE',
};

const Title = {
    [ExceptionTypes.DIAGNOSIS]: 'Clinical Exception',
    [ExceptionTypes.POLICY]: 'Clinical Exception',
    [ExceptionTypes.PLACE_OF_SERVICE]: 'Site of Care Exception',
};

const InlineMessageText = {
    [ExceptionTypes.DIAGNOSIS]: 'The diagnosis entered does not meet clinical policy.',
    [ExceptionTypes.POLICY]: 'The clinical information entered does not meet clinical policy.',
    [ExceptionTypes.PLACE_OF_SERVICE]:
        'The place of service entered does not meet site of care policy.',
};

const BodyText = {
    [ExceptionTypes.DIAGNOSIS]: (
        <p>
            If you wish to pursue an exception request, please provide the rationale as to why you
            are using this medication with the diagnosis. In certain cases, off-label uses of
            medications may be covered under medical policy where sufficient evidence exists to
            support use. Please be clear and robust in the rationale, including characteristics of
            the condition and previous tried and failed therapies.
        </p>
    ),
    [ExceptionTypes.POLICY]: (
        <>
            <p>
                The areas where information does not meet health plan clinical policy are
                highlighted for your information.
            </p>
            <p>
                If you wish to pursue an exception request, please provide the rationale as to why
                you are using this medication when it doesn&apos;t meet clinical policy.
            </p>
        </>
    ),
    [ExceptionTypes.PLACE_OF_SERVICE]: (
        <p>
            If you wish to pursue an exception request, please provide the rationale as to why you
            are using this place of service.
        </p>
    ),
};

export const FormExceptionDialog = ({
    isOpen,
    onSubmit,
    onSecondaryButtonClick,
    exceptionType,
    diagnosisList = [],
}) => (
    <Formik
        initialValues={{}}
        enableReinitialize
        validationSchema={Yup.object({
            diagnosis:
                exceptionType === ExceptionTypes.DIAGNOSIS
                    ? Yup.object().required(validationStrings.requiredField.errorMessage)
                    : Yup.object(),
            rationale: Yup.string().required(validationStrings.requiredField.errorMessage),
        })}
        onSubmit={(values, { setSubmitting, resetForm }) => {
            onSubmit(values);
            onSecondaryButtonClick();
            setSubmitting(false);
            resetForm();
        }}
    >
        {(formik) => (
            <DialogWithForm
                dialogTitle={Title[exceptionType]}
                headerIcon={IconNames.WARNING_SIGN}
                primaryButtonLabel="Continue With Exception"
                secondaryButtonLabel="Return To Form"
                width={800}
                isOpen={isOpen}
                isPrimaryButtonDisabled={!(formik.isValid && formik.dirty)}
                onSecondaryButtonClick={(e) => {
                    onSecondaryButtonClick();
                    formik.handleReset(e);
                }}
                dialogContent={
                    <>
                        <div className="margin__bottom--large">
                            <InlineMessage
                                intent={InlineMessage.Intent.WARNING}
                                label={InlineMessageText[exceptionType]}
                            />
                        </div>
                        <div className="margin__bottom--large">{BodyText[exceptionType]}</div>
                        {exceptionType === ExceptionTypes.DIAGNOSIS && (
                            <SuggestSingleSelectFormik
                                id="diagnosis"
                                label="Diagnosis"
                                required
                                values={diagnosisList}
                                isLoading={!diagnosisList}
                            />
                        )}
                        <TextAreaFormik id="rationale" label="Rationale" required />
                    </>
                }
            />
        )}
    </Formik>
);

FormExceptionDialog.ExceptionTypes = ExceptionTypes;

FormExceptionDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onSecondaryButtonClick: PropTypes.func.isRequired,
    exceptionType: PropTypes.oneOf(Object.values(ExceptionTypes)).isRequired,
    diagnosisList: PropTypes.arrayOf(PropTypes.shape({})),
};
